/**
 * Sentry error tracking provider for the ClubEzy auth client.
 *
 * Initialises Sentry on app startup and provides an Angular ErrorHandler
 * that forwards uncaught exceptions to Sentry.
 */
import {
  ErrorHandler,
  EnvironmentProviders,
  makeEnvironmentProviders,
  Injectable,
} from '@angular/core';
import * as Sentry from '@sentry/browser';
import { environment } from '@/environments/environment';
import { buildInfo } from '@/app/core/app-info/build-info.generated';
import {
  SENTRY_IGNORE_ERRORS,
  isChunkLoadError,
  reloadForStaleChunk,
} from './sentry-noise';

/**
 * Angular ErrorHandler that captures uncaught errors via Sentry.
 *
 * `Sentry.captureException` is a no-op when Sentry was never initialised, so
 * this stays installed in every environment and the reporting decision lives
 * in one place: `environment.sentry.enabled`.
 */
@Injectable()
export class SentryErrorHandler implements ErrorHandler {
  handleError(error: unknown): void {
    const extractedError =
      error && typeof error === 'object' && 'originalError' in error
        ? (error as { originalError: unknown }).originalError
        : error;

    // A tab opened before a deploy asks for a chunk that no longer exists:
    // reload onto the new build rather than report it (CLUB-2497).
    if (isChunkLoadError(extractedError) && reloadForStaleChunk()) return;

    Sentry.captureException(extractedError);
    console.error(extractedError);
  }
}

/**
 * Provides Sentry initialisation and error handler.
 * Add `provideSentry()` to the `providers` array in `app.config.ts`.
 */
export function provideSentry(): EnvironmentProviders {
  // `enabled` is deliberately NOT tied to `environment.production` — that
  // couples "is this an optimised build?" to "should this report?", which are
  // different questions and get answered differently per environment.
  if (environment.sentry.enabled && environment.sentry.dsn) {
    Sentry.init({
      dsn: environment.sentry.dsn,
      environment: environment.envName,
      // Ties each issue to the deployed build. Without it Sentry lumps every
      // deploy into one release and "which build regressed?" is unanswerable.
      release: buildInfo.version,
      ignoreErrors: SENTRY_IGNORE_ERRORS,
    });
  }

  return makeEnvironmentProviders([
    {
      provide: ErrorHandler,
      useClass: environment.sentry.enabled ? SentryErrorHandler : ErrorHandler,
    },
  ]);
}
